<template>
  <div
    v-if="editor"
    class="badiu-tiptap-toolbar d-flex flex-wrap align-items-center gap-1 px-2 py-1 bg-light border-bottom"
  >

    <!-- Group: History -->
    <div class="btn-group btn-group-sm" role="group" aria-label="History">
      <button
        type="button"
        class="btn btn-outline-secondary"
        title="Undo"
        @click="editor.chain().focus().undo().run()"
      >
        <i class="fas fa-undo fa-fw"></i>
      </button>
      <button
        type="button"
        class="btn btn-outline-secondary"
        title="Redo"
        @click="editor.chain().focus().redo().run()"
      >
        <i class="fas fa-redo fa-fw"></i>
      </button>
    </div>

    <div class="vr mx-1"></div>

    <!-- Group: Style -->
    <div class="btn-group btn-group-sm" role="group" aria-label="Text style">
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isActive('bold') ? 'active' : '']"
        title="Bold"
        @click="editor.chain().focus().toggleBold().run()"
      >
        <i class="fas fa-bold fa-fw"></i>
      </button>
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isActive('italic') ? 'active' : '']"
        title="Italic"
        @click="editor.chain().focus().toggleItalic().run()"
      >
        <i class="fas fa-italic fa-fw"></i>
      </button>
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isActive('underline') ? 'active' : '']"
        title="Underline"
        @click="editor.chain().focus().toggleUnderline().run()"
      >
        <i class="fas fa-underline fa-fw"></i>
      </button>
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isActive('strike') ? 'active' : '']"
        title="Strikethrough"
        @click="editor.chain().focus().toggleStrike().run()"
      >
        <i class="fas fa-strikethrough fa-fw"></i>
      </button>
    </div>

    <div class="vr mx-1"></div>

    <!-- Group: Headings -->
    <select
      class="form-select form-select-sm badiu-tiptap-block-select"
      :value="currentBlockType"
      @change="onBlockChange($event)"
    >
      <option value="paragraph">Paragraph</option>
      <option value="h2">Heading 2</option>
      <option value="h3">Heading 3</option>
      <option value="h4">Heading 4</option>
    </select>

    <div class="vr mx-1"></div>

    <!-- Group: Font family -->
    <select
      class="form-select form-select-sm badiu-tiptap-font-family-select"
      :value="currentFontFamily"
      @change="onFontFamilyChange($event)"
    >
      <option value="">Default font</option>
      <option value="Arial, sans-serif">Arial</option>
      <option value="'Times New Roman', serif">Times New Roman</option>
      <option value="'Courier New', monospace">Courier New</option>
      <option value="Georgia, serif">Georgia</option>
      <option value="Verdana, sans-serif">Verdana</option>
      <option value="Tahoma, sans-serif">Tahoma</option>
      <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
    </select>

    <!-- Group: Font size -->
    <select
      class="form-select form-select-sm badiu-tiptap-font-size-select"
      :value="currentFontSize"
      @change="onFontSizeChange($event)"
    >
      <option value="">Size</option>
      <option value="10px">10</option>
      <option value="12px">12</option>
      <option value="14px">14</option>
      <option value="16px">16</option>
      <option value="18px">18</option>
      <option value="20px">20</option>
      <option value="24px">24</option>
      <option value="28px">28</option>
      <option value="32px">32</option>
      <option value="36px">36</option>
      <option value="48px">48</option>
      <option value="72px">72</option>
    </select>

    <div class="vr mx-1"></div>

    <!-- Group: Alignment -->
    <div class="btn-group btn-group-sm" role="group" aria-label="Text alignment">
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isAlign('left') ? 'active' : '']"
        title="Align left"
        @click="editor.chain().focus().setTextAlign('left').run()"
      >
        <i class="fas fa-align-left fa-fw"></i>
      </button>
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isAlign('center') ? 'active' : '']"
        title="Center"
        @click="editor.chain().focus().setTextAlign('center').run()"
      >
        <i class="fas fa-align-center fa-fw"></i>
      </button>
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isAlign('right') ? 'active' : '']"
        title="Align right"
        @click="editor.chain().focus().setTextAlign('right').run()"
      >
        <i class="fas fa-align-right fa-fw"></i>
      </button>
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isAlign('justify') ? 'active' : '']"
        title="Justify"
        @click="editor.chain().focus().setTextAlign('justify').run()"
      >
        <i class="fas fa-align-justify fa-fw"></i>
      </button>
    </div>

    <div class="vr mx-1"></div>

    <!-- Group: Lists -->
    <div class="btn-group btn-group-sm" role="group" aria-label="Lists">
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isActive('bulletList') ? 'active' : '']"
        title="Bullet list"
        @click="editor.chain().focus().toggleBulletList().run()"
      >
        <i class="fas fa-list-ul fa-fw"></i>
      </button>
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isActive('orderedList') ? 'active' : '']"
        title="Ordered list"
        @click="editor.chain().focus().toggleOrderedList().run()"
      >
        <i class="fas fa-list-ol fa-fw"></i>
      </button>
    </div>

    <div class="vr mx-1"></div>

    <!-- Group: Insert -->
    <div class="btn-group btn-group-sm" role="group" aria-label="Insert">
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isActive('link') ? 'active' : '']"
        title="Insert / edit link"
        @click="$emit('open-link')"
      >
        <i class="fas fa-link fa-fw"></i>
      </button>
      <button
        type="button"
        class="btn btn-outline-secondary"
        title="Insert image"
        @click="$emit('open-image')"
      >
        <i class="fas fa-image fa-fw"></i>
      </button>
      <button
        type="button"
        class="btn btn-outline-secondary"
        title="Insert table"
        @click="$emit('insert-table')"
      >
        <i class="fas fa-table fa-fw"></i>
      </button>
    </div>

    <!-- Group: Tools (right side) -->
    <div class="btn-group btn-group-sm ms-auto" role="group" aria-label="Tools">
      <button
        type="button"
        class="btn btn-outline-secondary"
        title="View source code"
        @click="$emit('open-source')"
      >
        <i class="fas fa-code fa-fw"></i>
      </button>
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', fullscreen ? 'active' : '']"
        :title="fullscreen ? 'Restore size' : 'Expand editor'"
        @click="$emit('toggle-fullscreen')"
      >
        <i :class="['fas', 'fa-fw', fullscreen ? 'fa-compress' : 'fa-expand']"></i>
      </button>
    </div>

  </div>
</template>

<script>
/**
 * BadiuTiptapToolbar
 *
 * Isolated toolbar inspired by TinyMCE's layout:
 * logical groups separated by vertical dividers.
 *
 * Receives the editor instance via prop and emits events
 * handled by the parent component (BadiuTiptapEditor).
 */
import { computed, ref } from 'vue'

export default {
  name: "BadiuTiptapToolbar",

  props: {
    editor: {
      type: Object,
      default: null
    },

    fullscreen: {
      type: Boolean,
      default: false
    }
  },

  emits: [
    "open-link",
    "open-image",
    "open-source",
    "insert-table",
    "toggle-fullscreen"
  ],

  setup(props) {
    function isActive(name, attrs) {
      if (!props.editor) return false;
      return attrs
        ? props.editor.isActive(name, attrs)
        : props.editor.isActive(name);
    }

    function isAlign(align) {
      if (!props.editor) return false;
      return props.editor.isActive({ textAlign: align });
    }

    const currentBlockType = computed(() => {
      if (!props.editor) return "paragraph";

      if (props.editor.isActive("heading", { level: 2 })) return "h2";
      if (props.editor.isActive("heading", { level: 3 })) return "h3";
      if (props.editor.isActive("heading", { level: 4 })) return "h4";

      return "paragraph";
    });

    function onBlockChange(event) {
      const value = event.target.value;

      if (value === "paragraph") {
        props.editor.chain().focus().setParagraph().run();
      } else {
        const level = Number(value.replace("h", ""));
        props.editor.chain().focus().toggleHeading({ level }).run();
      }
    }

    const currentFontFamily = computed(() => {
      if (!props.editor) return "";
      return props.editor.getAttributes("textStyle").fontFamily || "";
    });

    const currentFontSize = computed(() => {
      if (!props.editor) return "";
      return props.editor.getAttributes("textStyle").fontSize || "";
    });

    function onFontFamilyChange(event) {
      const value = event.target.value;

      if (value) {
        props.editor.chain().focus().setMark("textStyle", { fontFamily: value }).run();
      } else {
        props.editor.chain().focus().setMark("textStyle", { fontFamily: null }).run();
      }
    }

    function onFontSizeChange(event) {
      const value = event.target.value;

      if (value) {
        props.editor.chain().focus().setMark("textStyle", { fontSize: value }).run();
      } else {
        props.editor.chain().focus().setMark("textStyle", { fontSize: null }).run();
      }
    }

    return {
      isActive,
      isAlign,
      currentBlockType,
      onBlockChange,
      currentFontFamily,
      currentFontSize,
      onFontFamilyChange,
      onFontSizeChange
    };
  }
};
</script>

<style>
/* ============================================================
 * Toolbar — escopada com .badiu-tiptap-toolbar para resistir
 * a CSS do host que possa quebrar o layout flex (ex.: regras
 * agressivas em form-select, btn-group, .row, etc.).
 * ============================================================ */
.badiu-tiptap-toolbar {
  display: flex !important;
  flex-wrap: wrap !important;
  align-items: center !important;
  gap: 4px !important;
  padding: 6px 8px !important;
  background: #f8f9fa !important;
  border-bottom: 1px solid #dee2e6 !important;
  width: 100% !important;
  box-sizing: border-box !important;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
}

.badiu-tiptap-toolbar .btn-group {
  display: inline-flex !important;
  vertical-align: middle !important;
  width: auto !important;
}

.badiu-tiptap-toolbar .btn {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: auto !important;
  flex: 0 0 auto !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
  line-height: 1.2 !important;
  background-color: #fff;
  color: #495057;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.badiu-tiptap-toolbar .btn-group > .btn {
  border-radius: 0 !important;
  margin-left: -1px;
}

.badiu-tiptap-toolbar .btn-group > .btn:first-child {
  border-top-left-radius: 0.25rem !important;
  border-bottom-left-radius: 0.25rem !important;
  margin-left: 0;
}

.badiu-tiptap-toolbar .btn-group > .btn:last-child {
  border-top-right-radius: 0.25rem !important;
  border-bottom-right-radius: 0.25rem !important;
}

.badiu-tiptap-toolbar .btn:hover {
  background-color: #e9ecef;
}

.badiu-tiptap-toolbar .btn.active {
  background-color: #dde5f0 !important;
  border-color: #9ec5fe !important;
  color: #084298 !important;
}

.badiu-tiptap-toolbar .vr {
  display: inline-block !important;
  align-self: stretch;
  width: 1px;
  min-height: 1.5em;
  background-color: rgba(0, 0, 0, 0.15);
  margin: 0 4px;
  opacity: 1;
}

.badiu-tiptap-toolbar select.form-select,
.badiu-tiptap-toolbar .form-select {
  display: inline-block !important;
  flex: 0 0 auto !important;
  padding: 0.25rem 1.75rem 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
  line-height: 1.2 !important;
  height: auto !important;
  vertical-align: middle !important;
  background-color: #fff;
  color: #495057;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  background-size: 12px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.badiu-tiptap-toolbar .badiu-tiptap-block-select {
  width: 130px !important;
  min-width: 130px !important;
}

.badiu-tiptap-toolbar .badiu-tiptap-font-family-select {
  width: 160px !important;
  min-width: 160px !important;
}

.badiu-tiptap-toolbar .badiu-tiptap-font-size-select {
  width: 80px !important;
  min-width: 80px !important;
}

.badiu-tiptap-toolbar .ms-auto {
  margin-left: auto !important;
}
</style>
